import * as pc from 'playcanvas';
import { getArenaBounds } from '../entities/arena.js';

// Current arena bounds (updated when arena changes)
let currentArenaBounds = null;

// Set the current arena theme for collision calculations
export function setArenaTheme(theme) {
    currentArenaBounds = getArenaBounds(theme);
}

// Check if a position is within arena bounds based on shape
function isWithinArenaBounds(x, z, bounds) {
    if (!bounds) {
        // Fallback to default circular arena
        return Math.sqrt(x * x + z * z) <= 10;
    }

    const { shape, radiusX, radiusZ } = bounds;

    switch (shape) {
        case 'square':
        case 'rectangle': {
            // Check if within rectangle bounds (with some margin)
            const marginX = radiusX * 0.9;
            const marginZ = radiusZ * 0.9;
            return Math.abs(x) <= marginX && Math.abs(z) <= marginZ;
        }
        case 'hexagon': {
            // Approximate hexagon as inscribed in circle
            const dist = Math.sqrt(x * x + z * z);
            const angle = Math.atan2(z, x);
            // Hexagon radius varies with angle
            const hexRadius = radiusZ * 0.9 * Math.cos(Math.PI / 6) / Math.cos((angle % (Math.PI / 3)) - Math.PI / 6);
            return dist <= Math.abs(hexRadius);
        }
        case 'octagon': {
            // Approximate octagon
            const dist = Math.sqrt(x * x + z * z);
            const angle = Math.atan2(z, x);
            const octRadius = radiusZ * 0.9 * Math.cos(Math.PI / 8) / Math.cos((angle % (Math.PI / 4)) - Math.PI / 8);
            return dist <= Math.abs(octRadius);
        }
        case 'oval': {
            // Ellipse equation: (x/a)^2 + (z/b)^2 <= 1
            const margin = 0.9;
            const normalizedX = x / (radiusX * margin);
            const normalizedZ = z / (radiusZ * margin);
            return (normalizedX * normalizedX + normalizedZ * normalizedZ) <= 1;
        }
        case 'circle':
        default: {
            const dist = Math.sqrt(x * x + z * z);
            return dist <= radiusZ * 0.9;
        }
    }
}

// Constrain position to arena bounds (exported for use by other systems)
export function constrainToArenaBounds(x, z, bounds = currentArenaBounds) {
    if (!bounds) {
        // Fallback to circular arena
        const dist = Math.sqrt(x * x + z * z);
        const maxDist = 9;
        if (dist > maxDist) {
            return { x: (x / dist) * maxDist, z: (z / dist) * maxDist };
        }
        return { x, z };
    }

    const { shape, radiusX, radiusZ } = bounds;
    const margin = 0.9;

    switch (shape) {
        case 'square':
        case 'rectangle': {
            const marginX = radiusX * margin;
            const marginZ = radiusZ * margin;
            return {
                x: Math.max(-marginX, Math.min(marginX, x)),
                z: Math.max(-marginZ, Math.min(marginZ, z))
            };
        }
        case 'hexagon':
        case 'octagon': {
            // For polygons, constrain to inscribed circle for simplicity
            const dist = Math.sqrt(x * x + z * z);
            const maxDist = radiusZ * margin * 0.866; // cos(30°) for hex, slightly conservative
            if (dist > maxDist) {
                return { x: (x / dist) * maxDist, z: (z / dist) * maxDist };
            }
            return { x, z };
        }
        case 'oval': {
            // Constrain to ellipse
            const marginX = radiusX * margin;
            const marginZ = radiusZ * margin;
            const normalizedX = x / marginX;
            const normalizedZ = z / marginZ;
            const ellipseDist = Math.sqrt(normalizedX * normalizedX + normalizedZ * normalizedZ);
            if (ellipseDist > 1) {
                return {
                    x: (x / ellipseDist),
                    z: (z / ellipseDist)
                };
            }
            return { x, z };
        }
        case 'circle':
        default: {
            const dist = Math.sqrt(x * x + z * z);
            const maxDist = radiusZ * margin;
            if (dist > maxDist) {
                return { x: (x / dist) * maxDist, z: (z / dist) * maxDist };
            }
            return { x, z };
        }
    }
}

// Check collision between player and boss
export function checkPlayerBossCollision(player, boss) {
    const playerPos = player.getPosition();
    const bossPos = boss.getPosition();

    const dx = playerPos.x - bossPos.x;
    const dz = playerPos.z - bossPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    return {
        colliding: distance < 2,
        distance,
        dx,
        dz
    };
}

// Push player away from boss
export function pushPlayerFromBoss(player, boss, dt) {
    const collision = checkPlayerBossCollision(player, boss);

    if (collision.colliding && collision.distance > 0) {
        const pushForce = (2 - collision.distance) * 10;
        const pushX = (collision.dx / collision.distance) * pushForce * dt;
        const pushZ = (collision.dz / collision.distance) * pushForce * dt;

        const playerPos = player.getPosition();
        const newX = playerPos.x + pushX;
        const newZ = playerPos.z + pushZ;

        // Constrain to arena bounds
        const constrained = constrainToArenaBounds(newX, newZ, currentArenaBounds);
        player.setPosition(constrained.x, playerPos.y, constrained.z);

        return true;
    }

    return false;
}

// Check if player is in attack range of boss
export function isPlayerInBossRange(player, boss, range = 2.5) {
    const collision = checkPlayerBossCollision(player, boss);
    return collision.distance < range;
}

// Check boss collision with pillars and platforms
export function handleBossCollision(boss, collisionObjects) {
    const bossPos = boss.getPosition();

    for (const obj of collisionObjects) {
        const dx = bossPos.x - obj.x;
        const dz = bossPos.z - obj.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        const minDist = obj.radius + 1.5; // boss is bigger

        if (dist < minDist && dist > 0) {
            boss.setPosition(
                obj.x + (dx / dist) * minDist,
                bossPos.y,
                obj.z + (dz / dist) * minDist
            );
        }
    }

    // Keep boss in arena using dynamic bounds
    const finalPos = boss.getPosition();
    const constrained = constrainToArenaBounds(finalPos.x, finalPos.z, currentArenaBounds);
    boss.setPosition(constrained.x, finalPos.y, constrained.z);
}

// Check if attack hit the boss
export function checkAttackHitBoss(player, boss, attackRange = 3.5) {
    const playerPos = player.getPosition();
    const bossPos = boss.getPosition();

    const dx = bossPos.x - playerPos.x;
    const dz = bossPos.z - playerPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    return {
        hit: distance < attackRange,
        distance,
        dx,
        dz
    };
}

// Check if attack hit a minion
export function checkAttackHitMinion(player, minion, attackRange = 2.5) {
    const playerPos = player.getPosition();
    const minionPos = minion.getPosition();

    const dx = minionPos.x - playerPos.x;
    const dz = minionPos.z - playerPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    return {
        hit: distance < attackRange,
        distance,
        dx,
        dz
    };
}

// Get distance between two entities
export function getDistance(entity1, entity2) {
    const pos1 = entity1.getPosition();
    const pos2 = entity2.getPosition();

    const dx = pos2.x - pos1.x;
    const dz = pos2.z - pos1.z;

    return {
        distance: Math.sqrt(dx * dx + dz * dz),
        dx,
        dz
    };
}

// Get closest player to an entity
export function getClosestPlayer(entity, players) {
    let closest = null;
    let minDistance = Infinity;

    for (const player of players) {
        if (player.health <= 0) continue;

        const { distance } = getDistance(entity, player);
        if (distance < minDistance) {
            minDistance = distance;
            closest = player;
        }
    }

    return { player: closest, distance: minDistance };
}
